// frontend/src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useEntitlement from "../hooks/useEntitlement";

import { readAuditSnapshotIndex, rebuildAuditSnapshotIndexFromPayloads } from "../lib/auditHistory";

const STORAGE_KEYS = {
  USER: "aifine_user",

  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",

  CONFIG: "aifine_config",

  // Per-business run state
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
  ANSWERS_PREFIX: "aifine_answers_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readLocalUser() {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEYS.USER), null);
  } catch {
    return null;
  }
}

function readSubscriptionTier() {
  const obj = readLocalUser();
  const tier = obj?.subscription_tier ?? obj?.subscriptionTier ?? obj?.tier ?? null;
  return tier ? String(tier) : "single";
}

function readRawPlanKey() {
  const obj = readLocalUser();
  const raw =
    obj?.raw_tier ??
    obj?.rawPlanKey ??
    obj?.plan_key ??
    obj?.planKey ??
    obj?.subscription_plan ??
    obj?.subscriptionPlan ??
    null;
  return raw ? String(raw) : null;
}

function writeSubscriptionTier(nextTier, rawPlanKey = null) {
  try {
    const clean = String(nextTier || "").toLowerCase() === "top" ? "top" : "single";
    const prev = readLocalUser() || {};
    const obj = {
      ...prev,
      subscription_tier: clean,
      raw_tier: rawPlanKey ?? prev?.raw_tier ?? null,
      updated_at_iso: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(obj));
    return clean;
  } catch {
    return String(nextTier || "single");
  }
}

function isTopTier(tier) {
  return String(tier || "").toLowerCase() === "top";
}

function loadBusinessIndex() {
  try {
    const idx = safeParse(localStorage.getItem(STORAGE_KEYS.BUSINESS_INDEX), []);
    return Array.isArray(idx) ? idx : [];
  } catch {
    return [];
  }
}

function loadBusinessPayload(businessId) {
  try {
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
  } catch {
    return null;
  }
}

function setActiveBusinessId(businessId) {
  try {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID, String(businessId));
  } catch {
    // ignore
  }
}

function getActiveBusinessId() {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function setConfigShim(payload) {
  try {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function jurLabel(j) {
  return Array.isArray(j) && j.length ? j.join(", ") : null;
}

function countAnsweredValues(obj) {
  if (!obj || typeof obj !== "object") return 0;
  let n = 0;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE") n += 1;
  }
  return n;
}

function readProgressForBusiness(bid) {
  try {
    const prog = safeParse(localStorage.getItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${bid}`), null);
    if (prog && typeof prog === "object") return prog;
    return null;
  } catch {
    return null;
  }
}

function readAnswersForBusiness(bid) {
  try {
    const a = safeParse(localStorage.getItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${bid}`), null);
    return a && typeof a === "object" ? a : null;
  } catch {
    return null;
  }
}

function clearRunStateForBusiness(bid) {
  try {
    localStorage.removeItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${bid}`);
    localStorage.removeItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${bid}`);
  } catch {
    // ignore
  }
}

function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function safePct(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return null;
  return clamp(Math.round(n), 0, 100);
}

function formatWhen(iso) {
  try {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function useResizeObserver(ref) {
  const [rect, setRect] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref?.current;
    if (!el) return;

    let raf = null;
    const ro = new ResizeObserver((entries) => {
      const r = entries?.[0]?.contentRect;
      if (!r) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setRect({ width: Math.round(r.width), height: Math.round(r.height) });
      });
    });

    ro.observe(el);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [ref]);

  return rect;
}

function ScoreTrendChart({ points }) {
  const containerRef = useRef(null);
  const { width } = useResizeObserver(containerRef);

  const [hover, setHover] = useState(null);

  const h = 380;
  const padL = 60;
  const padR = 18;
  const padT = 18;
  const padB = 52;

  const pts = Array.isArray(points) ? points : [];
  const hasData = pts.length >= 2;

  const domain = useMemo(() => {
    if (!hasData) return null;
    const xs = pts.map((p) => p.t).filter((x) => Number.isFinite(x));
    const ys = pts.map((p) => p.score).filter((y) => Number.isFinite(y));
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length ? Math.max(...xs) : 1;
    const minY = ys.length ? Math.min(...ys) : 0;
    const maxY = ys.length ? Math.max(...ys) : 100;

    const yLo = clamp(Math.floor(minY / 5) * 5 - 5, 0, 100);
    const yHi = clamp(Math.ceil(maxY / 5) * 5 + 5, 0, 100);
    const safeHi = yHi <= yLo ? clamp(yLo + 10, 0, 100) : yHi;

    return { minX, maxX, minY: yLo, maxY: safeHi };
  }, [hasData, pts]);

  const svgW = Math.max(560, width || 0);

  const xOf = (t, i) => {
    if (!domain) return padL;
    const span = domain.maxX - domain.minX;
    if (span <= 0) {
      const u = pts.length <= 1 ? 0 : i / (pts.length - 1);
      return padL + u * (svgW - padL - padR);
    }
    const u = (t - domain.minX) / span;
    return padL + clamp(u, 0, 1) * (svgW - padL - padR);
  };

  const yOf = (score) => {
    if (!domain) return h - padB;
    const span = domain.maxY - domain.minY;
    const u = span <= 0 ? 0 : (score - domain.minY) / span;
    return padT + (1 - clamp(u, 0, 1)) * (h - padT - padB);
  };

  const pathD = useMemo(() => {
    if (!hasData || !domain) return "";
    const parts = pts.map((p, i) => {
      const x = xOf(p.t, i);
      const y = yOf(p.score);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    });
    return parts.join(" ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, domain, pts, svgW]);

  const areaD = useMemo(() => {
    if (!hasData || !domain) return "";
    const x0 = xOf(pts[0].t, 0);
    const y0 = yOf(pts[0].score);
    const yBase = h - padB;
    const segs = pts.map((p, i) => `L ${xOf(p.t, i).toFixed(2)} ${yOf(p.score).toFixed(2)}`).join(" ");
    const xLast = xOf(pts[pts.length - 1].t, pts.length - 1);
    return `M ${x0.toFixed(2)} ${y0.toFixed(2)} ${segs} L ${xLast.toFixed(2)} ${yBase.toFixed(
      2
    )} L ${x0.toFixed(2)} ${yBase.toFixed(2)} Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, domain, pts, svgW]);

  const yTicks = useMemo(() => {
    if (!domain) return [];
    const lo = domain.minY;
    const hi = domain.maxY;
    const step = 10;
    const out = [];
    for (let v = lo; v <= hi; v += step) out.push(v);
    return out;
  }, [domain]);

  const xLabels = useMemo(() => {
    if (!domain || pts.length < 2) return [];
    const desired = 4;
    const out = [];
    for (let i = 0; i < desired; i += 1) {
      const idx = Math.round((i / (desired - 1)) * (pts.length - 1));
      const p = pts[idx];
      if (!p) continue;
      out.push({ idx, t: p.t, iso: p.created_at_iso });
    }
    const seen = new Set();
    return out.filter((x) => {
      if (seen.has(x.idx)) return false;
      seen.add(x.idx);
      return true;
    });
  }, [domain, pts]);

  return (
    <div ref={containerRef} className="relative w-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="relative flex items-start justify-between gap-6">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Score over time</div>
          <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Assessment Compliance Trend</div>
          <div className="mt-1 text-sm text-slate-600">Hover any point to see the audit score at that time.</div>
        </div>
        {/* “Line / Blue Trend” pill intentionally removed */}
      </div>

      <div className="relative mt-5">
        {!hasData ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
            No trend yet. Complete at least two audits to see progress over time.
          </div>
        ) : (
          <div className="relative">
            {hover ? (
              <div
                className="pointer-events-none absolute z-10 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
                style={{
                  left: clamp(hover.x - 95, 8, Math.max(8, svgW - 190)),
                  top: clamp(hover.y - 72, 8, h - 92),
                }}
              >
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Audit</div>
                <div className="mt-1 text-sm font-extrabold text-slate-900">{hover.score}% score</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{formatWhen(hover.created_at_iso)}</div>
              </div>
            ) : null}

            <svg width={svgW} height={h} className="block w-full select-none">
              <defs>
                <linearGradient id="aifineArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity="0.20" />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0.02" />
                </linearGradient>

                <filter id="aifineGlow" x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {yTicks.map((v) => {
                const y = yOf(v);
                return (
                  <g key={`y-${v}`}>
                    <line x1={padL} x2={svgW - padR} y1={y} y2={y} stroke="rgba(15, 23, 42, 0.08)" strokeWidth="1" />
                    <text x={padL - 10} y={y + 4} textAnchor="end" fontSize="11" fill="rgba(15,23,42,0.55)">
                      {v}
                    </text>
                  </g>
                );
              })}

              <text x={padL} y={padT - 2} fontSize="11" fill="rgba(15,23,42,0.55)">
                Score
              </text>

              <line
                x1={padL}
                x2={svgW - padR}
                y1={h - padB}
                y2={h - padB}
                stroke="rgba(15, 23, 42, 0.18)"
                strokeWidth="1"
              />

              {xLabels.map((x) => {
                const px = xOf(x.t, x.idx);
                return (
                  <g key={`x-${x.idx}`}>
                    <line
                      x1={px}
                      x2={px}
                      y1={h - padB}
                      y2={h - padB + 6}
                      stroke="rgba(15,23,42,0.25)"
                      strokeWidth="1"
                    />
                    <text x={px} y={h - padB + 24} textAnchor="middle" fontSize="11" fill="rgba(15,23,42,0.55)">
                      {(() => {
                        try {
                          const d = new Date(x.iso);
                          if (Number.isNaN(d.getTime())) return "—";
                          return d.toLocaleDateString();
                        } catch {
                          return "—";
                        }
                      })()}
                    </text>
                  </g>
                );
              })}

              <text x={svgW - padR} y={h - 14} textAnchor="end" fontSize="11" fill="rgba(15,23,42,0.55)">
                Time
              </text>

              <path d={areaD} fill="url(#aifineArea)" stroke="none" />

              <path
                d={pathD}
                fill="none"
                stroke="#2563EB"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#aifineGlow)"
              />

              {pts.map((p, i) => {
                const x = xOf(p.t, i);
                const y = yOf(p.score);
                return (
                  <g
                    key={p.snapshot_id || i}
                    onMouseEnter={() => setHover({ ...p, x, y })}
                    onMouseLeave={() => setHover(null)}
                    onMouseMove={() => setHover({ ...p, x, y })}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={x} cy={y} r={12} fill="rgba(37, 99, 235, 0.16)" />
                    <circle cx={x} cy={y} r={6} fill="#2563EB" />
                    <circle cx={x} cy={y} r={14} fill="transparent" />
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const entitlement = useEntitlement?.() || {};
  const entLoading = Boolean(entitlement.loading);

  const entTierRaw = entitlement.tier != null ? String(entitlement.tier) : null;

  const entPlanKey =
    entitlement.rawPlanKey ??
    entitlement.planKey ??
    entitlement.plan_key ??
    entitlement.plan ??
    null;

  const [tier, setTier] = useState(() => {
    try {
      return readSubscriptionTier();
    } catch {
      return "single";
    }
  });

  useEffect(() => {
    if (entLoading) return;

    const inferred = isTopTier(entTierRaw) ? "top" : "single";
    const rawPlanKeyForStorage = entPlanKey != null ? String(entPlanKey) : readRawPlanKey();

    if (String(inferred).toLowerCase() !== String(tier).toLowerCase()) {
      const persisted = writeSubscriptionTier(inferred, rawPlanKeyForStorage);
      setTier(persisted);
    } else {
      writeSubscriptionTier(tier, rawPlanKeyForStorage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entLoading, entTierRaw, entPlanKey]);

  const top = useMemo(() => isTopTier(tier), [tier]);

  const [activePayload, setActivePayload] = useState(null);
  const [inProgressRows, setInProgressRows] = useState([]);

  const [snapshotSeries, setSnapshotSeries] = useState([]);
  const [latestSnapshot, setLatestSnapshot] = useState(null);

  const effectiveBusinessId = useMemo(() => {
    if (!top) return STANDARD_SINGLE_ID;
    return getActiveBusinessId() || "";
  }, [top]);

  const refreshSnapshots = () => {
    try {
      let rows = readAuditSnapshotIndex({ businessId: effectiveBusinessId });

      if (!Array.isArray(rows) || rows.length === 0) {
        try {
          rebuildAuditSnapshotIndexFromPayloads();
          rows = readAuditSnapshotIndex({ businessId: effectiveBusinessId });
        } catch {
          rows = rows || [];
        }
      }

      const sorted = Array.isArray(rows) ? rows.slice() : [];
      const latest = sorted.length ? sorted[0] : null;

      const chrono = sorted.slice().reverse();

      const points = chrono
        .map((r) => {
          const score = safePct(r?.compliance_score_pct);
          const created = r?.created_at_iso || null;
          const t = Date.parse(created || "");
          if (score == null || !Number.isFinite(t)) return null;
          return {
            snapshot_id: String(r?.snapshot_id || ""),
            created_at_iso: created,
            score,
            t,
          };
        })
        .filter(Boolean);

      setSnapshotSeries(points);
      setLatestSnapshot(
        latest
          ? {
              snapshot_id: latest?.snapshot_id ? String(latest.snapshot_id) : "",
              created_at_iso: latest?.created_at_iso || null,
              compliance_score_pct: safePct(latest?.compliance_score_pct),
            }
          : null
      );
    } catch {
      setSnapshotSeries([]);
      setLatestSnapshot(null);
    }
  };

  const refreshBusinessState = () => {
    if (!top) {
      const active = getActiveBusinessId();
      if (!active) setActiveBusinessId(STANDARD_SINGLE_ID);

      const cfg = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
      setActivePayload(cfg || null);

      const bid = STANDARD_SINGLE_ID;
      const prog = readProgressForBusiness(bid);
      const ans = readAnswersForBusiness(bid);

      const total = Number(prog?.totalQuestions) || 0;
      const completed = Number(prog?.completedCount) || 0;
      const answeredFallback = countAnsweredValues(ans);

      const isIncompleteByProgress = total > 0 && completed < total;
      const isIncompleteByAnswersOnly = !prog && answeredFallback > 0;

      if (isIncompleteByProgress || isIncompleteByAnswersOnly) {
        const payload = loadBusinessPayload(bid) || cfg || null;
        setInProgressRows([
          {
            business_id: bid,
            entity_name: payload?.clientName || "Unnamed",
            jurisdictions: payload?.jurisdictions || [],
            completed: isIncompleteByProgress ? completed : answeredFallback,
            total: isIncompleteByProgress ? total : total || 0,
            resumeAt: isIncompleteByProgress ? Number(prog.currentIndex) + 1 : null,
            updated_at_iso: prog?.updated_at_iso || payload?.updated_at_iso || null,
          },
        ]);
      } else {
        setInProgressRows([]);
      }

      refreshSnapshots();
      return;
    }

    const idx = loadBusinessIndex();
    const activeId = getActiveBusinessId();

    let resolvedActiveId = activeId;
    if (!resolvedActiveId && idx.length) {
      const first = idx[0]?.business_id;
      if (first) {
        setActiveBusinessId(first);
        resolvedActiveId = first;
      }
    }

    const active = resolvedActiveId ? loadBusinessPayload(resolvedActiveId) : null;
    setActivePayload(active || null);

    const rows = [];
    for (const entry of idx) {
      const bid = entry?.business_id;
      if (!bid) continue;

      const payload = loadBusinessPayload(bid);
      const prog = readProgressForBusiness(bid);
      const ans = readAnswersForBusiness(bid);

      const total = Number(prog?.totalQuestions) || 0;
      const completed = Number(prog?.completedCount) || 0;

      const answeredFallback = countAnsweredValues(ans);

      const isIncompleteByProgress = total > 0 && completed < total;
      const isIncompleteByAnswersOnly = !prog && answeredFallback > 0;

      if (isIncompleteByProgress || isIncompleteByAnswersOnly) {
        rows.push({
          business_id: bid,
          entity_name: payload?.clientName || entry.entity_name || "Unnamed",
          jurisdictions: payload?.jurisdictions || entry.jurisdictions || [],
          completed: isIncompleteByProgress ? completed : answeredFallback,
          total: isIncompleteByProgress ? total : total || 0,
          resumeAt: isIncompleteByProgress ? Number(prog.currentIndex) + 1 : null,
          updated_at_iso: prog?.updated_at_iso || payload?.updated_at_iso || entry.updated_at_iso || null,
        });
      }
    }

    rows.sort((a, b) => {
      const ta = a.updated_at_iso ? new Date(a.updated_at_iso).getTime() : 0;
      const tb = b.updated_at_iso ? new Date(b.updated_at_iso).getTime() : 0;
      return tb - ta;
    });

    setInProgressRows(rows);
    refreshSnapshots();
  };

  useEffect(() => {
    refreshBusinessState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top]);

  useEffect(() => {
    const onFocus = () => refreshBusinessState();

    const onStorage = (e) => {
      if (!e || e.key == null) return;
      const k = String(e.key);

      if (k === STORAGE_KEYS.ACTIVE_BUSINESS_ID) return refreshBusinessState();
      if (k === STORAGE_KEYS.BUSINESS_INDEX) return refreshBusinessState();

      if (k.startsWith(STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX)) return refreshBusinessState();
      if (k.startsWith(STORAGE_KEYS.PROGRESS_PREFIX)) return refreshBusinessState();
      if (k.startsWith(STORAGE_KEYS.ANSWERS_PREFIX)) return refreshBusinessState();

      if (k === STORAGE_KEYS.CONFIG && !top) return refreshBusinessState();

      if (k === "aifine_audit_snapshot_index_v1") return refreshBusinessState();
      if (k.startsWith("aifine_audit_snapshot_payload_")) return refreshBusinessState();
      if (k.startsWith("aifine_audit_snapshot_payload_snap_")) return refreshBusinessState();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top]);

  const activeEntityName = activePayload?.clientName ? String(activePayload.clientName) : null;
  const activeJur = jurLabel(activePayload?.jurisdictions);

  const latestScore = latestSnapshot?.compliance_score_pct;
  const latestWhen = latestSnapshot?.created_at_iso;

  const handleDeleteInProgress = (businessId, label) => {
    const name = label || "this in-progress assessment";
    const ok = window.confirm(
      `Delete ${name}?\n\nThis removes only saved in-progress answers/progress for this business. Completed audits and reports are not deleted.`
    );
    if (!ok) return;

    clearRunStateForBusiness(businessId);
    refreshBusinessState();
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl px-8 py-10">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
              <p className="mt-1 text-sm text-slate-600">Run a new Assessment Audit or resume an in-progress audit.</p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {activeEntityName ? (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    Legal Entity: {activeEntityName}
                  </span>
                ) : null}

                {activeJur ? (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    Jurisdictions: {activeJur}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {top ? (
                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=new")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  New / Switch Business →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=rerun")}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Run New Assessment Audit →
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <ScoreTrendChart points={snapshotSeries} />
            </div>

            <div className="lg:col-span-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm h-full">
                <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Most recent audit</div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">Compliance score</div>
                  <div className="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
                    {typeof latestScore === "number" ? `${latestScore}%` : "—"}
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    {latestWhen ? (
                      <>
                        Performed: <span className="font-semibold text-slate-900">{formatWhen(latestWhen)}</span>
                      </>
                    ) : (
                      "No completed audits yet."
                    )}
                  </div>
                </div>

                <div className="mt-5 text-sm text-slate-600">
                  {typeof latestScore === "number"
                    ? "Keep running audits to track progress over time."
                    : "Run your first assessment to generate a baseline score."}
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => navigate("/reports")}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View Reports →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {top ? (
            <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-black text-amber-950">In-progress Assessments (Multi-entity)</div>
                  <div className="mt-1 text-xs text-amber-900/80">
                    Resume any business below. Each in-progress run is saved independently.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=new")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  New / Switch Business →
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-amber-200 bg-white">
                <div className="grid grid-cols-12 bg-amber-100/50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-amber-900/70">
                  <div className="col-span-4">Business</div>
                  <div className="col-span-3">Progress</div>
                  <div className="col-span-3">Last Saved</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {inProgressRows.length ? (
                  inProgressRows.map((r) => (
                    <div
                      key={r.business_id}
                      className="grid grid-cols-12 items-center px-4 py-3 border-t border-amber-100 text-sm"
                    >
                      <div className="col-span-4">
                        <div className="font-semibold text-slate-900">{r.entity_name}</div>
                        <div className="text-xs text-slate-500">
                          {Array.isArray(r.jurisdictions) && r.jurisdictions.length ? r.jurisdictions.join(", ") : "—"}
                        </div>
                      </div>

                      <div className="col-span-3 text-slate-700 text-sm">
                        {r.total > 0 ? (
                          <>
                            {r.completed}/{r.total}
                            {typeof r.resumeAt === "number" ? ` · Resume at Q${r.resumeAt}` : ""}
                          </>
                        ) : (
                          <>{r.completed} answered · Resume</>
                        )}
                      </div>

                      <div className="col-span-3 text-xs text-slate-500">
                        {r.updated_at_iso ? new Date(r.updated_at_iso).toLocaleString() : "—"}
                      </div>

                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const payload = loadBusinessPayload(r.business_id);
                            if (!payload) {
                              navigate("/start-audit?mode=rerun");
                              return;
                            }

                            setActiveBusinessId(r.business_id);
                            setConfigShim(payload);
                            navigate("/assessment");
                          }}
                          className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-800"
                        >
                          Resume →
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteInProgress(r.business_id, r.entity_name)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-50"
                          title="Deletes only in-progress answers/progress for this business"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-4 text-sm text-slate-600">No in-progress assessments found yet.</div>
                )}
              </div>
            </div>
          ) : inProgressRows.length ? (
            <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-black text-amber-950">In-progress Assessment</div>
                  <div className="mt-1 text-xs text-amber-900/80">Your progress is saved. Resume where you left off.</div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/start-audit?mode=rerun")}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Continue Setup →
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-amber-200 bg-white">
                <div className="grid grid-cols-12 bg-amber-100/50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-amber-900/70">
                  <div className="col-span-6">Business</div>
                  <div className="col-span-4">Progress</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {inProgressRows.map((r) => (
                  <div
                    key={r.business_id}
                    className="grid grid-cols-12 items-center px-4 py-3 border-t border-amber-100 text-sm"
                  >
                    <div className="col-span-6">
                      <div className="font-semibold text-slate-900">{r.entity_name}</div>
                      <div className="text-xs text-slate-500">
                        {Array.isArray(r.jurisdictions) && r.jurisdictions.length ? r.jurisdictions.join(", ") : "—"}
                      </div>
                    </div>

                    <div className="col-span-4 text-slate-700 text-sm">
                      {r.total > 0 ? (
                        <>
                          {r.completed}/{r.total}
                          {typeof r.resumeAt === "number" ? ` · Resume at Q${r.resumeAt}` : ""}
                        </>
                      ) : (
                        <>{r.completed} answered · Resume</>
                      )}
                    </div>

                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => navigate("/assessment")}
                        className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-800"
                      >
                        Resume →
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteInProgress(r.business_id, r.entity_name)}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-50"
                        title="Deletes only in-progress answers/progress for this business"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-10 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}